import type { ComponentProps } from 'react';
import React from 'react';

import type { Breakpoint } from '../../utilities/useViewport';
import type {
  ShellContentProps,
  ShellFooterProps,
  ShellHeaderProps,
  ShellProps,
} from '../Shell/Shell';
import { styled } from '../../stitches.config';
import { useViewport } from '../../utilities/useViewport';
import {
  type DrawerWidthDescriptor,
  getDrawerPaddingValue,
  useDrawerWidthContext,
} from '../Drawer/Drawer';
import { Shell, ShellContent, ShellFooter, ShellHeader } from '../Shell/Shell';

function getEffectiveDescriptor(
  drawerAware: boolean | DrawerWidthDescriptor | undefined,
  contextDescriptor: DrawerWidthDescriptor | null,
): DrawerWidthDescriptor | true | undefined {
  if (drawerAware === undefined || drawerAware === false) return undefined;
  if (typeof drawerAware === 'object') return drawerAware;
  return contextDescriptor ?? true;
}

const getDrawerPadding = (
  breakpoint: Breakpoint,
  effectiveDescriptor: DrawerWidthDescriptor | true | undefined,
) =>
  effectiveDescriptor !== undefined
    ? getDrawerPaddingValue(effectiveDescriptor, breakpoint)
    : undefined;

const PaneHeaderContainer = styled(ShellHeader);
PaneHeaderContainer.displayName = 'PaneHeaderContainer';

export type PaneHeaderProps = ShellHeaderProps & {
  /**
   * If true, the header will be aware of the drawer and will adjust its padding accordingly.
   */
  drawerAware?: boolean;
};

export function PaneHeader({ drawerAware = false, ...remaining }: PaneHeaderProps) {
  const { breakpoint } = useViewport();
  const { descriptor: contextDescriptor } = useDrawerWidthContext();
  const effectiveDescriptor = getEffectiveDescriptor(drawerAware, contextDescriptor);

  return (
    <PaneHeaderContainer
      css={{ paddingRight: getDrawerPadding(breakpoint, effectiveDescriptor) }}
      {...remaining}
    />
  );
}

const PaneContentContainer = styled(ShellContent);
PaneContentContainer.displayName = 'PaneContentContainer';

export type PaneContentProps = ShellContentProps & {
  /**
   * If true, the content will be aware of the drawer and will adjust its padding accordingly.
   */
  drawerAware?: boolean;
};

export function PaneContent({
  children,
  drawerAware = false,
  gutter = 'none',
  spacing = 16,
  ref,
  ...remaining
}: PaneContentProps) {
  const { breakpoint } = useViewport();
  const { descriptor: contextDescriptor } = useDrawerWidthContext();
  const effectiveDescriptor = getEffectiveDescriptor(drawerAware, contextDescriptor);

  return (
    <PaneContentContainer
      gutter={gutter}
      spacing={spacing}
      ref={ref}
      css={{
        paddingRight: getDrawerPadding(breakpoint, effectiveDescriptor),
      }}
      {...remaining}
    >
      {children}
    </PaneContentContainer>
  );
}

const PaneFooterContainer = styled(ShellFooter);
PaneFooterContainer.displayName = 'PaneFooterContainer';

export type PaneFooterProps = ShellFooterProps & {
  /**
   * Provide primary actions for your drawer.
   */
  actions?: React.ReactNode;
  /**
   * Fallback to `children` if `actions` or `secondaryActions` are not provided.
   */
  children?: React.ReactNode;
  /**
   * If true, the footer will be aware of the drawer and will adjust its padding accordingly.
   */
  drawerAware?: boolean;
  /**
   * Provide secondary actions, positioned at the start of the footer.
   */
  secondaryActions?: React.ReactNode;
} & ComponentProps<typeof PaneFooterContainer>;

export function PaneFooter({
  actions,
  children,
  secondaryActions,
  drawerAware = false,
  ...remaining
}: PaneFooterProps) {
  const { breakpoint } = useViewport();
  const { descriptor: contextDescriptor } = useDrawerWidthContext();
  const effectiveDescriptor = getEffectiveDescriptor(drawerAware, contextDescriptor);

  return (
    <PaneFooterContainer
      start={secondaryActions}
      end={actions || children}
      css={{
        paddingRight: getDrawerPadding(breakpoint, effectiveDescriptor),
      }}
      {...remaining}
    />
  );
}

export const PaneContainer = styled(Shell, {
  gridArea: 'pane',
  width: '100%',

  '@mobile': {
    widthAll: '100%',
  },

  variants: {
    preset: {
      full: {},
      intermediate: {
        '@mobile': {
          height: '100%',
        },

        '@notMobile': {
          paddingTop: '$32',
        },
      },
      narrow: {
        '@mobile': {
          height: '100%',
        },

        '@notMobile': {
          paddingTop: '$32',
        },
      },
    },
  },
});
PaneContainer.displayName = 'PaneContainer';

export type ShellPresetProp = 'narrow' | 'intermediate' | 'full';

export type PaneProps = ShellProps & {
  /**
   * When true, Pane sets --drawer-padding so drawer-aware header/content/footer
   * match the default drawer width. When an object (preset/width/minWidth/maxWidth),
   * uses that to set --drawer-padding so padding matches the Drawer's actual size.
   */
  drawerAware?: boolean | DrawerWidthDescriptor;
  preset?: ShellPresetProp;
} & ComponentProps<typeof PaneContainer>;

const getPaneMaxWidth = (preset: ShellPresetProp, breakpoint: Breakpoint) => {
  switch (preset) {
    case 'intermediate':
      return breakpoint !== 'mobile' ? 1024 : undefined;
    case 'narrow':
      return breakpoint !== 'mobile' ? 760 : undefined;
    default:
      return 'full';
  }
};

export function Pane({ children, drawerAware, preset = 'full', ...remaining }: PaneProps) {
  const { breakpoint } = useViewport();
  const { descriptor: contextDescriptor } = useDrawerWidthContext();
  const effectiveDescriptor = getEffectiveDescriptor(drawerAware, contextDescriptor);

  return (
    <PaneContainer
      preset={preset}
      maxWidth={getPaneMaxWidth(preset, breakpoint)}
      widthMode={preset === 'full' ? 'full' : 'centered'}
      css={{
        paddingRight: getDrawerPadding(breakpoint, effectiveDescriptor),
      }}
      {...remaining}
    >
      {children}
    </PaneContainer>
  );
}
